import { create } from 'zustand'
import { apiClient } from '../api/client'
import { bridge } from '../bridge'

export interface User {
  id: string
  maxUserId: bigint
  phone: string | null
  isPhoneVerified: boolean
  isPremium: boolean
  isBanned: boolean
  /** Имя из MAX initData — для префилла анкеты. */
  maxFullName?: string | null
  maxUsername?: string | null
  maxAvatarUrl?: string | null
  /** https://max.ru/@username при известном нике. */
  maxProfileUrl?: string | null
}

export interface Profile {
  userId: string
  name: string
  moderationStatus: string
  isPhoneVerified?: boolean
}

export interface AuthState {
  user: User | null
  profile: Profile | null
  token: string | null
  isLoading: boolean
  error: string | null
  initialize: () => Promise<void>
  /** Обновить профиль из API (после создания/редактирования анкеты), чтобы роутер не сбрасывал на onboarding. */
  refreshProfile: () => Promise<void>
  logout: () => void
}

const AUTH_TOKEN_KEY = 'maximur_auth_token'

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  token: null,
  isLoading: true,
  error: null,

  refreshProfile: async () => {
    const token =
      get().token ??
      (typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_KEY) : null)
    if (!token) {
      console.warn('[Auth] refreshProfile: no token')
      return
    }
    apiClient.setToken(token)
    const p = await apiClient.get<Profile & { phoneMasked?: string | null; isPhoneVerified?: boolean }>(
      '/api/profile/me',
    )
    set((s) => ({
      user: s.user
        ? {
            ...s.user,
            id: p.userId,
            isPhoneVerified: Boolean(p.isPhoneVerified),
            phone: p.phoneMasked ?? s.user.phone,
          }
        : {
            id: p.userId,
            maxUserId: BigInt(0),
            phone: p.phoneMasked ?? null,
            isPhoneVerified: Boolean(p.isPhoneVerified),
            isPremium: false,
            isBanned: false,
          },
      profile: {
        userId: p.userId,
        name: p.name,
        moderationStatus: p.moderationStatus,
        isPhoneVerified: Boolean(p.isPhoneVerified),
      },
    }))
  },

  initialize: async () => {
    try {
      set({ isLoading: true, error: null })
      if (import.meta.env.DEV) {
        console.log('[Auth] Initializing...')
      }

      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY)
      if (storedToken) {
        if (import.meta.env.DEV) {
          console.log('[Auth] Using stored token')
        }
        apiClient.setToken(storedToken)
        try {
          const storedProfile = await apiClient.get<
            Profile & { phoneMasked?: string | null; isPhoneVerified?: boolean }
          >('/api/profile/me')
          set({
            user: {
              id: storedProfile.userId,
              maxUserId: BigInt(0),
              phone: storedProfile.phoneMasked ?? null,
              isPhoneVerified: Boolean(storedProfile.isPhoneVerified),
              isPremium: false,
              isBanned: false,
            },
            profile: {
              userId: storedProfile.userId,
              name: storedProfile.name,
              moderationStatus: storedProfile.moderationStatus,
              isPhoneVerified: Boolean(storedProfile.isPhoneVerified),
            },
            token: storedToken,
            isLoading: false,
          })
          return
        } catch (profileErr) {
          console.warn('[Auth] Stored token is invalid, re-authenticating', profileErr)
          localStorage.removeItem(AUTH_TOKEN_KEY)
          apiClient.clearToken()
        }
      }

      try {
        if (import.meta.env.DEV) {
          console.log('[Auth] Getting init data from bridge')
        }
        let initDataRaw: string

        try {
          initDataRaw = bridge.getInitDataRaw()
          if (import.meta.env.DEV) {
            console.log('[Auth] Got init data, calling /api/auth/session')
          }
        } catch (bridgeErr) {
          console.error('[Auth] Bridge error:', bridgeErr)
          // If bridge fails (e.g., SES error), use fallback
          const fallbackData = `fallback_${Date.now()}`
          if (import.meta.env.DEV) {
            console.log('[Auth] Using fallback init data')
          }
          initDataRaw = fallbackData
        }

        const response = await apiClient.post<{
          user: Omit<User, 'maxUserId'> & { maxUserId: string }
          profile: Profile | null
          token: string
        }>('/api/auth/session', {
          initData: initDataRaw,
        })

        if (import.meta.env.DEV) {
          console.log('[Auth] Auth successful, setting token')
        }
        apiClient.setToken(response.token)
        localStorage.setItem(AUTH_TOKEN_KEY, response.token)

        set({
          user: {
            ...response.user,
            maxUserId: BigInt(response.user.maxUserId),
          },
          profile: response.profile,
          token: response.token,
          isLoading: false,
        })
      } catch (authErr) {
        console.error('[Auth] Auth error:', authErr)

        let errMsg = 'Failed to authenticate'
        if (authErr instanceof Error) {
          errMsg = authErr.message
        } else if (authErr && typeof authErr === 'object') {
          // Handle API error response object
          const errObj = authErr as any
          errMsg = errObj.message || errObj.error || String(authErr)
        } else if (typeof authErr === 'string') {
          errMsg = authErr
        }

        if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(errMsg)) {
          errMsg =
            'Не удаётся связаться с сервером. Проверьте интернет и попробуйте снова чуть позже.'
        }

        // Handle SES errors specifically
        if (errMsg.includes('SES') || errMsg.includes('intrinsics')) {
          console.error('[Auth] SES Security error detected')
        }

        // If auth fails, still mark as not loading so app can show error
        set({
          error: errMsg || 'Failed to authenticate',
          isLoading: false,
        })
      }
    } catch (err) {
      console.error('[Auth] Init error:', err)
      let message = 'Failed to initialize'
      if (err instanceof Error) {
        message = err.message
      } else if (err && typeof err === 'object') {
        const errObj = err as any
        message = errObj.message || String(err)
      } else if (typeof err === 'string') {
        message = err
      }
      set({
        error: message,
        isLoading: false,
      })
    }
  },

  logout: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    apiClient.clearToken()
    set({
      user: null,
      profile: null,
      token: null,
      error: null,
    })
  },
}))
